import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper';
import ListHeader from '@/common/components/ListHeader';
import Switch from '@/common/components/Switch';
import useDebounce from '@/common/hooks/useDebounce';
import { useFilterParams } from '@/common/hooks/useFilterParams';
import { useMineCompany } from '@/features/company/hooks/useMineCompany';
import useEstablishmentCompany from '@/features/establishments/hooks/useEstablishmentCompany';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { BuildingIcon, MoonIcon, SunIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useEstablishmentMap } from '../../stores/useEstablishmentMap.store';
import EstablishmentMapActions from './components/Actions';
import EstablishmentMapCardList from './components/CardList';
import EstablishmentMapHeader from './components/Header';
import { MarkerTooltip } from './components/MarkerTooltip';
import { brasiliaCoordinates, loaderOptions, mapOptions } from './constants/constant';
import { hoveredMarker } from './constants/markers/hoveredMarker';
import { singleMarker } from './constants/markers/singleMarker';
import { stackedMarker } from './constants/markers/stackedMarker';
import { useEstablishmentClusters } from './hooks/useEstablishmentClusters';
import { Cluster } from './types/cluster';
import { EstablishmentClustersFilterProps } from './types/clusterFilters';
import { isPositionVisible } from './utils/mapUtils';

const Map = () => {
  const { isLoaded, loadError } = useJsApiLoader(loaderOptions);
  const { filterParams, setFilterParams } = useFilterParams();

  const { data: company } = useMineCompany();

  const {
    action,
    colorScheme,
    updateColorScheme,
    map,
    updateMap,
    center,
    updateCenter,
    hoveredMarkerId,
    updateHoveredMarkerId,
    updateHoveredOnList,
    boundsChangedOnCardClick,
    updateBoundsChangedOnCardClick,
    hoveredOnList,
    hoveredEstablishmentData,
    updateClusteredEstablishmentsIds,
    isAnimating,
  } = useEstablishmentMap();

  const showProductPrice = action?.name === 'price_search' && Object.keys(action?.properties || {}).length > 0;

  const [search, setSearch] = useDebounce(filterParams.search || '');

  const {
    data: clusteredEstablishments,
    dataUpdatedAt: clusteredEstablishmentUpdatedAt,
    error: establishmentClustersError = {} as any,
    refetch: refetchEstablishmentClusters,
  } = useEstablishmentClusters({ ...filterParams, search } as EstablishmentClustersFilterProps, action);

  const identifiedClusters = useMemo(
    () => {
      const identifiedClusters = clusteredEstablishments?.map(cluster => {
        return {
          ...cluster,
          name: `marker-${cluster.latitude}-${cluster.longitude}-${cluster.establishment_count}`,
        };
      });

      updateClusteredEstablishmentsIds(clusteredEstablishments?.map((cluster) => cluster.establishment_company_id));

      return identifiedClusters;
    },
    [clusteredEstablishmentUpdatedAt],
  );

  const hoveredEstablishmentNotFoundInIdentifiedClusters = !!hoveredMarkerId && !identifiedClusters?.find((cluster) => cluster.establishment_company_id === hoveredMarkerId);
  const hoveredEstablishmentPositionIsVisible = hoveredEstablishmentNotFoundInIdentifiedClusters && map && isPositionVisible(map, {
    lat: Number(hoveredEstablishmentData?.general_information?.address.latitude),
    lng: Number(hoveredEstablishmentData?.general_information?.address.longitude),
  });

  const { data: establishment, isPending: isPendingEstablishmentData } = useEstablishmentCompany(hoveredMarkerId, !!hoveredMarkerId && !hoveredOnList && !hoveredEstablishmentNotFoundInIdentifiedClusters);

  const [hasCalledBoundsChange, setHasCalledBoundsChange] = useState(false);

  const handleGetInitialZoom = () => {
    if (center?.lat === brasiliaCoordinates.lat && center.lng === brasiliaCoordinates.lng) {
      return 6; // Case when the browser has no access to location
    } else {
      return 15;
    }
  };

  const handleMarkerClick = (cluster: Cluster) => {
    if (cluster.establishment_count > 1) {
      const newZoom = map?.getZoom()! + 1;
      map?.setZoom(newZoom);
      map?.setCenter({ lat: cluster.latitude, lng: cluster.longitude });
    }
  };

  const handleOnMouseOver = (cluster: Cluster & { name: string; }) => {
    if (cluster.establishment_count === 1) {
      updateHoveredOnList(false);
      updateHoveredMarkerId(cluster.establishment_company_id);
    }
  };

  const handleMapBoundsChange = useCallback((cameFromOnBound?: boolean, clearHoveredMarkerId = true) => {
    if (cameFromOnBound) {
      setHasCalledBoundsChange(true);
    }

    if (clearHoveredMarkerId) {
      updateHoveredMarkerId('');
    }

    if (clearHoveredMarkerId) {
      updateBoundsChangedOnCardClick(false);
      updateHoveredMarkerId('');
    }

    const bounds = map?.getBounds();

    if (bounds) {
      const northEast = bounds.getNorthEast();
      const southWest = bounds.getSouthWest();

      setFilterParams((prevParams) => ({
        ...prevParams,
        top_left_lat: northEast.lat(),
        top_left_lng: southWest.lng(),
        bottom_right_lat: southWest.lat(),
        bottom_right_lng: northEast.lng(),
      }));
    }
  }, [map, setFilterParams]);

  const setFilterBounds = useCallback((bounds: { top_left_lat: number; top_left_lng: number; bottom_right_lat: number; bottom_right_lng: number; }) => {
    setFilterParams((prevParams) => ({
      ...prevParams,
      top_left_lat: bounds.top_left_lat,
      top_left_lng: bounds.top_left_lng,
      bottom_right_lat: bounds.bottom_right_lat,
      bottom_right_lng: bounds.bottom_right_lng,
    }));
  }, [map, setFilterParams]);

  useEffect(() => {
    updateCenter(brasiliaCoordinates);

    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const { latitude, longitude } = position.coords;
          updateCenter({ lat: latitude, lng: longitude });
        },
        (error) => {
          updateCenter(brasiliaCoordinates);
          console.error('Error getting location', error);
        }
      );
    } else {
      updateCenter(brasiliaCoordinates);
      console.error('Geolocation is not supported by this browser.');
    }
  }, []);

  if (!isLoaded || loadError || !center) {
    return (
      <>
        <ListHeader
          Icon={<BuildingIcon className="w-10 h-10" strokeWidth={1.5} />}
          title="Rede credenciada"
        />
        <div className="flex w-full h-[calc(100vh-192px)] justify-center items-center">Carregando mapa...</div>
      </>
    );
  }

  return (
    <div className="flex relative h-screen -m-8">
      <div className="absolute z-30 top-5 left-5">
        <EstablishmentMapHeader />
      </div>

      <EstablishmentMapCardList
        setSearch={setSearch}
        companyKind={company?.kind}
        setFilterBounds={setFilterBounds}
      />

      <div className='absolute z-20 right-8 top-6'>
        <Switch
          className='h-8 w-14 border px-[3px] data-[state=unchecked]:border-yellow-500 data-[state=unchecked]:bg-[#343233] data-[state=checked]:bg-[#343233] data-[state=checked]:border-primary-blue'
          thumbClassName='h-6 w-6 bg-yellow-500 transition-transform data-[state=checked]:translate-x-6 data-[state=checked]:bg-primary-blue'
          Icon={colorScheme === 'light' ? <SunIcon color='#343233' size={14} /> : <MoonIcon size={14} />}
          onCheckedChange={(checked) => updateColorScheme(checked ? 'dark' : 'light')}
          defaultChecked={colorScheme === 'dark'}
        />
      </div>

      <EstablishmentMapActions />

      <ErrorMessageWrapper
        statusCode={establishmentClustersError?.status}
        refetchFunction={refetchEstablishmentClusters}
      >
        <GoogleMap
          mapContainerStyle={{
            flex: 1,
          }}
          center={center}
          zoom={handleGetInitialZoom()}
          onLoad={(mapInstance) => {
            updateMap(mapInstance);
          }}
          options={{
            ...mapOptions({ colorScheme }),
            backgroundColor: colorScheme === 'dark' ? '#343233' : '#ECE7E4',
          }}
          onZoomChanged={() => {
            if (!isAnimating) {
              handleMapBoundsChange();
            }
          }}
          onDragEnd={() => handleMapBoundsChange()}
          onDragStart={() => updateHoveredMarkerId('')}
          onClick={() => updateHoveredMarkerId('')}
          onBoundsChanged={() => {
            if (!hasCalledBoundsChange || boundsChangedOnCardClick) {
              handleMapBoundsChange(true, !boundsChangedOnCardClick);
            }
          }}
        >
          {(hoveredEstablishmentNotFoundInIdentifiedClusters && hoveredEstablishmentData && hoveredEstablishmentPositionIsVisible) && (
            <div key={`marker-${hoveredEstablishmentData?.general_information?.address?.latitude}-${hoveredEstablishmentData?.general_information?.address?.longitude}-${1}-only`}>
              <Marker
                position={{
                  lat: Number(hoveredEstablishmentData?.general_information?.address?.latitude),
                  lng: Number(hoveredEstablishmentData?.general_information?.address?.longitude),
                }}
                icon={{
                  url: hoveredMarker({ colorScheme }),
                  anchor: new window.google.maps.Point(16, 50),
                }}
                zIndex={2}
              />

              <MarkerTooltip
                cluster={{
                  latitude: Number(hoveredEstablishmentData?.general_information?.address?.latitude),
                  longitude: Number(hoveredEstablishmentData?.general_information?.address?.longitude),
                }}
                establishment={hoveredEstablishmentData}
                isLoadingEstablishmentData={false}
              />
            </div>
          )}

          {identifiedClusters?.map((cluster) => (
            <div key={`${cluster.name}`}>
              <Marker
                position={{
                  lat: Number(cluster.latitude),
                  lng: Number(cluster.longitude),
                }}
                icon={{
                  url: hoveredMarkerId === cluster.establishment_company_id
                    ? hoveredMarker({ cluster, showPrice: showProductPrice, colorScheme })
                    : cluster.establishment_count > 1
                      ? stackedMarker({ cluster, showPrice: showProductPrice, colorScheme })
                      : singleMarker({ cluster, showPrice: showProductPrice, colorScheme }),
                }}
                zIndex={hoveredMarkerId === cluster.establishment_company_id ? 3 : 1}
                onClick={() => handleMarkerClick(cluster)}
                onMouseOver={() => handleOnMouseOver(cluster)}
                onMouseOut={(event) => {
                  if (!((event.domEvent as MouseEvent).relatedTarget as HTMLDivElement).closest('.establishment-map-tooltip')) {
                    updateHoveredMarkerId('');
                  }
                }}
              />

              {hoveredMarkerId === cluster.establishment_company_id && (
                <MarkerTooltip
                  cluster={cluster}
                  companyKind={company?.kind}
                  establishment={hoveredOnList ? hoveredEstablishmentData : establishment}
                  isLoadingEstablishmentData={hoveredOnList ? false : isPendingEstablishmentData}
                />
              )}
            </div>
          ))}
        </GoogleMap>
      </ErrorMessageWrapper>
    </div>
  );
};

export default Map;
